import { zeroErrorMessage } from "@/lib/zero-error";
import { useState } from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { Tables, TablesInsert, TablesUpdate } from "@/integrations/neon/types";
import { generateUUID } from "@/lib/utils";
import { toast } from "sonner";

export type Company = Tables<"companies">;
export type CompanyInsert = TablesInsert<"companies">;
export type CompanyUpdate = TablesUpdate<"companies">;

type ZeroCompanyRow = {
  id: string;
  name: string;
  number?: string | null;
  invoiceAddress?: string | null;
  types?: Company["types"];
  isMainCompany?: boolean | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  deletedAt?: number | string | null;
  version?: number | null;
  rentmanID?: number | null;
  language?: string | null;
};

type MutationOptions<T> = { onSuccess?: (data: T) => void };
type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyCompany(row: ZeroCompanyRow): Company {
  return {
    id: row.id,
    name: row.name,
    number: row.number ?? null,
    invoice_address: row.invoiceAddress ?? null,
    types: row.types ?? [],
    is_main_company: row.isMainCompany ?? false,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    deleted_at: row.deletedAt ? toLegacyTimestamp(row.deletedAt) : null,
    version: row.version ?? null,
    rentman_id: row.rentmanID ?? null,
    language: row.language ?? null,
  };
}

function toZeroCompanyInput(company: CompanyInsert, id: string) {
  return {
    id,
    name: company.name,
    number: company.number ?? null,
    invoiceAddress: company.invoice_address ?? null,
    language: company.language ?? null,
    types: company.types ?? [],
    isMainCompany: company.is_main_company ?? false,
    rentmanID: company.rentman_id ?? null,
  };
}

function toZeroCompanyUpdate(id: string, updates: CompanyUpdate) {
  return {
    id,
    ...(updates.name !== undefined ? { name: updates.name } : {}),
    ...(updates.number !== undefined ? { number: updates.number } : {}),
    ...(updates.invoice_address !== undefined
      ? { invoiceAddress: updates.invoice_address }
      : {}),
    ...(updates.language !== undefined ? { language: updates.language } : {}),
    ...(updates.types !== undefined ? { types: updates.types } : {}),
    ...(updates.is_main_company !== undefined
      ? { isMainCompany: updates.is_main_company }
      : {}),
    ...(updates.rentman_id !== undefined
      ? { rentmanID: updates.rentman_id }
      : {}),
  };
}

async function reportServerMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

export function useCompanies(includeDeleted = false) {
  const [data, details] = useQuery(
    appQueries.companies.list({ includeDeleted }),
  );
  const companies = ((data ?? []) as ZeroCompanyRow[]).map(toLegacyCompany);

  return {
    data: companies,
    isLoading: details.type !== "complete" && companies.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCompany(id: string | undefined) {
  const query = id ? appQueries.companies.byID({ id }) : undefined;
  const [data, details] = useQuery(query, { enabled: !!id });

  return {
    data: data ? toLegacyCompany(data as ZeroCompanyRow) : null,
    isLoading: !!id && details.type !== "complete" && !data,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateCompany() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (company: CompanyInsert) => {
    const input = toZeroCompanyInput(company, generateUUID());
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.companies.create(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Company create was rejected",
      );
      return toLegacyCompany({ ...input, createdAt: "", updatedAt: "" });
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (company: CompanyInsert, options?: MutationOptions<Company>) => {
      void run(company)
        .then((data) => {
          toast.success("Company created successfully");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to create company: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdateCompany() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    id,
    updates,
  }: {
    id: string;
    updates: CompanyUpdate;
  }) => {
    const input = toZeroCompanyUpdate(id, updates);
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.companies.update(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Company update was rejected",
      );
      return toLegacyCompany({
        ...input,
        name: input.name ?? "",
        createdAt: "",
        updatedAt: "",
      });
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: { id: string; updates: CompanyUpdate },
      options?: MutationOptions<Company>,
    ) => {
      void run(input)
        .then((data) => {
          toast.success("Company updated successfully");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to update company: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useSoftDeleteCompany() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (id: string) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.companies.softDelete({ id }));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Company delete was rejected",
      );
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (id: string, options?: { onSuccess?: () => void }) => {
      void run(id)
        .then(() => {
          toast.success("Company moved to trash");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to delete company: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useRestoreCompany() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (id: string) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.companies.restore({ id }));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Company restore was rejected",
      );
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (id: string, options?: { onSuccess?: () => void }) => {
      void run(id)
        .then(() => {
          toast.success("Company restored");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to restore company: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}
